import React from 'react';
import {
     CAccordion,
     CAccordionBody,
     CAccordionHeader,
     CAccordionItem,
     CButton,
     CCardBody,
     CCol,
     CContainer,
     CRow,
     CSpinner,
} from '@coreui/react-pro';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { COURSE_DIRECT_PATH, SUBSCRIBER_PATH, TRAINING_CLASSIFICATION, GUEST_PATH, LOCAL_ORG_USER, MY_ORG_PATH } from '../../../../../../constants';
import { getFirstSellingGroupProductFromProduct, scrollToTopOffset, tryParseJSON } from '../../../../../../utils';
import TooltipWithIcon from '../../../../../General/Tooltips/TooltipWithIcon';
import TrainingIntro from '../../../../../General/TrainingIntro/TrainingIntro';
import PurchaseEventCard from '../../../../Subscriber/MyLeaning/CourseTypes/OnlineLiveTraining/PurchaseEventCard';
import YourEventCard from '../../../../Subscriber/MyLeaning/CourseTypes/OnlineLiveTraining/YourEventCard';
import { getEventsByTrainingType } from '../../../../Subscriber/MyLeaning/CourseTypes/OnlineLiveTraining/Config';
import { useActiveProduct } from '../../../../../../helpers/customHook';
import { fetchSubscriberLimitProductEventsRequest } from '../../../../../../helpers/handleFunctionGeneral';
import YourEventCreditCard from '../../../../Subscriber/MyLeaning/CourseTypes/OnlineLiveTraining/YourEventCreditCard';
import { fetchOrganizationCoursesRequest } from '../../../OrganizationCourses/actions';

const OnlineLiveTraining = () => {
     const dispatch = useDispatch();
     const navigate = useNavigate();
     const location = useLocation();
     const product = useActiveProduct();
     const orgUserLocalStorage = tryParseJSON(localStorage.getItem(LOCAL_ORG_USER));
     const organizationId = orgUserLocalStorage?.organizationId;
     const locationPath = location.pathname;
     const { sellingGroupCategorySlug, slug } = useParams();
     const organizationCourses = useSelector((state) => state.subscriber.organizationCourses);
     const scrollToCourseGroup = useRef([]);
     const MAX_EVENT_SHOW = 3;

     const [isLoading, setIsLoading] = useState(false);
     const [productEvents, setProductEvents] = useState([]);
     const [viewAllUpcoming, setViewAllUpcoming] = useState([]);
     const [windowSize, setWindowSize] = useState(getWindowSize());
     const [showBtnPurchaseCustom, setShowBtnPurchaseCustom] = useState(true);

     const preventPurchaseText = product.preventPurchase ? product.preventPurchaseText || 'This product is not available now' : null;

     //Get all product Events by Product slug
     const fetchProductEvents = () => {
          if (product?.id) {
               dispatch(
                    fetchSubscriberLimitProductEventsRequest({
                         id: product?.id,
                         organizationId,
                         setFunction: setProductEvents,
                         setIsLoading,
                    })
               );
          }
     };

     const fetchOrganizationCourses = () => {
          if (organizationId) {
               dispatch(fetchOrganizationCoursesRequest(organizationId));
          }
     };

     useEffect(() => {
          fetchOrganizationCourses();
          fetchProductEvents();
     }, []); // eslint-disable-line react-hooks/exhaustive-deps

     useEffect(() => {
          scrollToTopOffset();

          const handleWindowResize = () => {
               setWindowSize(getWindowSize());
          };
          window.addEventListener('resize', handleWindowResize);

          return () => {
               window.removeEventListener('resize', handleWindowResize);
          };
     }, []);

     function getWindowSize() {
          const { innerWidth } = window;
          return { innerWidth };
     }

     const handleCollapseFirstEl = (e) => {
          const collapseBtn = document.getElementsByClassName('accordion-button')[0];
          const isCollapsed = collapseBtn?.classList?.contains('collapsed');
          setShowBtnPurchaseCustom(isCollapsed);
     };

     const handleCourseGroup = () => {
          if (windowSize.innerWidth <= 767) {
               scrollToCourseGroup.current[0].scrollIntoView();
          }
     };

     const handleClickPurchase = () => {
          localStorage.setItem(COURSE_DIRECT_PATH, locationPath);

          const sellingGroupProduct = getFirstSellingGroupProductFromProduct(product?.sellingGroupProducts);
          const slugSellingGroup = sellingGroupProduct?.sellingGroup?.slug;

          if (slugSellingGroup) {
               navigate(GUEST_PATH.SELLING_GROUPS_DETAIL.replace(':slug', slugSellingGroup));
          }
     };

     const productDetails = product
          ? {
                 ...product,
                 events: productEvents.map((event) => {
                      event.course = organizationCourses?.find((course) => course.eventId === event.id);
                      return event;
                 }),
                 eventCredits: organizationCourses.filter((course) => course.productId === product?.id && course.isCredit),
            }
          : null;

     const listCourseGroups = [
          {
               type: 'UpcomingTrainingEvents',
               name: TRAINING_CLASSIFICATION.COURSE_UPCOMING.replace('{genericPublicName}', productDetails?.productType?.genericPublicName),
               description: 'View trainings that you have purchased access to or have been given acess to by an Organization.',
               events: productDetails?.events?.length ? getEventsByTrainingType(productDetails.events, 'UpcomingTrainingEvents') : [],
               eventCredits: productDetails?.eventCredits || [],
               pastEvents: productDetails?.events?.length ? getEventsByTrainingType(productDetails.events, 'PassTrainingEvents') : [],
               additionalEvents: productDetails?.events ? getEventsByTrainingType(productDetails.events, 'PurchaseAdditional') : [],
               notice: '<p>If you have purchased access to or been assigned to an upcoming training, it should appear here. Contact your Organization Leader or  <a href="">customer support</a> if you have questions.</p>',
          },
          // {
          //      type: 'PassTrainingEvents',
          //      name: TRAINING_CLASSIFICATION.COURSE_PAST.replace('{genericPublicName}', productDetails?.productType?.genericPublicName),
          //      badge: 'Complete Surveys to Finalize Your Training!',
          //      description:
          //           'These past trainings require you to complete a survey to finalize your attendance. Do so by clicking into the training details.',
          //      events: productDetails?.events?.length ? getEventsByTrainingType(productDetails.events, 'PassTrainingEvents') : [],
          //      notice: '<p>Trainings will appear here after your training leader confirms your attendance in the system. You must have participated for 90% of the training for it to appear here. Contact <a href="">customer support</a> if you have questions.</p>',
          // },
     ];

     const courseName = (product?.relatedProductProductId ? product.relatedPageOrgHeadline : product?.pageOrgHeadline) || product?.name;
     const subHeadline =
          (product?.relatedProductProductId ? product.relatedPageOrgSubHeadline : product?.pageOrgSubHeadline) || product?.shortDescription;
     const bodyText = (product?.relatedProductProductId ? product?.relatedPageOrgBodyText : product?.pageOrgBodyText) || product?.description;
     const image = product?.relatedProductProductId ? product?.relatedPageOrgImage : product?.pageOrgImage;

     const checkPreventPurchase = product.preventPurchase;

     if (isLoading) {
          return (
               <div className="text-center">
                    <CSpinner color="primary" />
               </div>
          );
     }

     return (
          <section className="my-professional role-play-training">
               <CContainer>
                    <div className="container-content">
                         <CRow>
                              <TrainingIntro title={courseName} subTitle={subHeadline} description={bodyText} image={image} />
                         </CRow>
                         <CRow>
                              <CCol>
                                   <CAccordion className="accordion-training-content training-day" activeItemKey={1}>
                                        {listCourseGroups.map((courseGroup, index) => {
                                             const hasCourseEvents =
                                                  courseGroup.events.length > 0 &&
                                                  courseGroup.events.some((item) => item.event || item.course?.length || item.course?.id);

                                             const hasEventCredits = courseGroup.eventCredits.length;

                                             const additionalEvents = courseGroup.additionalEvents.filter(
                                                  (event) => event.remainingSeats >= product.organizationMin
                                             );

                                             return (
                                                  <Fragment key={index}>
                                                       <CAccordionItem
                                                            itemKey={index + 1}
                                                            key={index}
                                                            className="learning-course-name"
                                                            ref={(element) => {
                                                                 scrollToCourseGroup.current[index] = element;
                                                            }}
                                                            onClick={handleCourseGroup}
                                                       >
                                                            <CAccordionHeader
                                                                 className="d-flex justify-content-between align-items-center"
                                                                 onClick={(e) => {
                                                                      if (courseGroup.type === 'UpcomingTrainingEvents') {
                                                                           handleCollapseFirstEl(e);
                                                                      } else {
                                                                           setShowBtnPurchaseCustom(false);
                                                                      }
                                                                 }}
                                                            >
                                                                 <div className="d-flex align-items-center description-tooltip">
                                                                      <div>
                                                                           <p>{courseGroup.name}</p>
                                                                           <div
                                                                                className="btn-tooltip"
                                                                                onClick={(e) => {
                                                                                     e.stopPropagation();
                                                                                }}
                                                                           >
                                                                                <TooltipWithIcon
                                                                                     content={courseGroup?.description}
                                                                                     trigger="mouseenter focus"
                                                                                />
                                                                           </div>
                                                                      </div>
                                                                 </div>
                                                            </CAccordionHeader>
                                                            <CAccordionBody>
                                                                 <CCardBody>
                                                                      {/* Current */}
                                                                      <div className="training-events">
                                                                           <h4>Your Training Events</h4>
                                                                           {hasCourseEvents || hasEventCredits ? (
                                                                                <div className="training-events-calender">
                                                                                     {courseGroup.events.map((event, eventIndex) => {
                                                                                          const linkToDetail = `${SUBSCRIBER_PATH.ORGANIZATIONS_COURSE_EVENT_DETAILS.replace(
                                                                                               ':slug',
                                                                                               slug
                                                                                          ).replace(':eventId', event.id)}`;

                                                                                          return (
                                                                                               <YourEventCard
                                                                                                    key={event.id}
                                                                                                    event={event}
                                                                                                    linkToDetail={linkToDetail}
                                                                                               />
                                                                                          );
                                                                                     })}

                                                                                     {courseGroup.eventCredits.map((organizationCourse) => {
                                                                                          const linkToDetail = `${MY_ORG_PATH.ORGANIZATIONS_COURSE_CATEGORIES_DETAILS_EVENT_CREDITS.replace(
                                                                                               ':slug',
                                                                                               slug
                                                                                          ).replace(':courseId', organizationCourse?.id)}`;

                                                                                          return (
                                                                                               <Fragment key={organizationCourse.id}>
                                                                                                    <YourEventCreditCard
                                                                                                         course={organizationCourse}
                                                                                                         linkToDetail={linkToDetail}
                                                                                                    />
                                                                                               </Fragment>
                                                                                          );
                                                                                     })}
                                                                                </div>
                                                                           ) : (
                                                                                <p>No Course!</p>
                                                                           )}
                                                                      </div>

                                                                      {/* Past */}
                                                                      <div className="training-events">
                                                                           <h4>Your Past Training Events</h4>
                                                                           {courseGroup.pastEvents?.length > 0 &&
                                                                           courseGroup.pastEvents.some(
                                                                                (item) => item.event || item.course?.length || item.course?.id
                                                                           ) ? (
                                                                                <div className="training-events-calender">
                                                                                     {courseGroup.pastEvents.map((event, eventIndex) => {
                                                                                          const linkToDetail = `${SUBSCRIBER_PATH.ORGANIZATIONS_COURSE_EVENT_DETAILS.replace(
                                                                                               ':slug',
                                                                                               slug
                                                                                          ).replace(':eventId', event.id)}`;

                                                                                          return (
                                                                                               <YourEventCard
                                                                                                    key={event.id}
                                                                                                    event={event}
                                                                                                    linkToDetail={linkToDetail}
                                                                                               />
                                                                                          );
                                                                                     })}
                                                                                </div>
                                                                           ) : (
                                                                                <p>No Course!</p>
                                                                           )}
                                                                      </div>

                                                                      {/* Upcoming */}

                                                                      <div className="training-events">
                                                                           {courseGroup.type === 'UpcomingTrainingEvents' && (
                                                                                <h4>Purchase Additional Training Events</h4>
                                                                           )}

                                                                           {!viewAllUpcoming.includes(index) &&
                                                                                additionalEvents.length > MAX_EVENT_SHOW && (
                                                                                     <CButton
                                                                                          className="view-all-training btn-outline-none-highlight"
                                                                                          onClick={() =>
                                                                                               setViewAllUpcoming([...viewAllUpcoming, index])
                                                                                          }
                                                                                     >
                                                                                          {courseGroup.type === 'UpcomingTrainingEvents'
                                                                                               ? 'VIEW ALL UPCOMING TRAINING'
                                                                                               : 'VIEW ALL PAST LIVE TRAINING'}
                                                                                     </CButton>
                                                                                )}

                                                                           <div className="training-events-calender">
                                                                                {courseGroup.additionalEvents?.length > 0 && !checkPreventPurchase ? (
                                                                                     <>
                                                                                          {additionalEvents.map((event, eventIndex) => {
                                                                                               const showViewAllUpcoming =
                                                                                                    viewAllUpcoming.includes(index) ||
                                                                                                    eventIndex < MAX_EVENT_SHOW;

                                                                                               if (!showViewAllUpcoming) {
                                                                                                    return <Fragment key={event.id}></Fragment>;
                                                                                               }

                                                                                               const linkToDetail = `${SUBSCRIBER_PATH.ORGANIZATIONS_COURSE_EVENT_DETAILS.replace(
                                                                                                    ':sellingGroupCategorySlug',
                                                                                                    sellingGroupCategorySlug
                                                                                               )
                                                                                                    .replace(':slug', slug)
                                                                                                    .replace(':eventId', event.id)}`;

                                                                                               const checkShowEvent =
                                                                                                    event.id ||
                                                                                                    event.customEventGenericPublicName !== null;

                                                                                               return (
                                                                                                    <Fragment key={event.id}>
                                                                                                         {checkShowEvent && (
                                                                                                              <PurchaseEventCard
                                                                                                                   event={event}
                                                                                                                   linkToDetail={linkToDetail}
                                                                                                                   handleClickPurchase={
                                                                                                                        handleClickPurchase
                                                                                                                   }
                                                                                                                   product={product}
                                                                                                              />
                                                                                                         )}
                                                                                                    </Fragment>
                                                                                               );
                                                                                          })}
                                                                                     </>
                                                                                ) : (
                                                                                     courseGroup.type === 'UpcomingTrainingEvents' && (
                                                                                          <p>No Course!</p>
                                                                                     )
                                                                                )}
                                                                           </div>
                                                                      </div>
                                                                 </CCardBody>
                                                            </CAccordionBody>
                                                       </CAccordionItem>
                                                       {courseGroup.type === 'UpcomingTrainingEvents' &&
                                                            showBtnPurchaseCustom &&
                                                            !preventPurchaseText && (
                                                                 <CButton
                                                                      onClick={handleClickPurchase}
                                                                      className="btn-blue-dark w-100 btn-h-50 btn-round-9 button-purchase-custom-event"
                                                                 >
                                                                      PURCHASE CUSTOM EVENT DATE
                                                                 </CButton>
                                                            )}
                                                  </Fragment>
                                             );
                                        })}
                                   </CAccordion>
                              </CCol>
                         </CRow>
                    </div>
               </CContainer>
          </section>
     );
};

export default OnlineLiveTraining;
